import React, { useState, useEffect } from 'react';
import {
  VStack,
  Box,
  Text,
  Button,
  Spinner,
  useToast,
  Flex,
  Divider,
  Badge,
  Icon,
  Link,
  Input,
  InputGroup,
  InputRightElement,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Textarea,
  useColorModeValue
} from '@chakra-ui/react';
import { FaWhatsapp, FaPaperPlane, FaChevronDown, FaSync } from 'react-icons/fa';
import { FiDownload } from 'react-icons/fi';
import moment from 'moment';
import WhatsAppService from '../../services/whatsapp.service';
import whatsappConfig from '../../config/whatsapp.config';

const { messageTemplates: MESSAGE_TEMPLATES } = whatsappConfig;

// Legacy templates for reference
const OLD_TEMPLATES = [
  // Templates moved to whatsapp.config.js
];

const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  
  // If the phone already has a "+" prefix, convert to WhatsApp format
  if (phone.startsWith('+')) {
    // Remove the "+" and return the number
    return phone.substring(1);
  }
  
  // Remove all non-digits
  const cleaned = phone.replace(/\D/g, '');
  
  // If the number is longer than 10 digits, assume it already includes a country code
  if (cleaned.length > 10) {
    return cleaned;
  }
  
  // Default: Assume it's an Indian number if it's 10 digits
  if (cleaned.length === 10) {
    return `91${cleaned}`;
  }
  
  // For shorter numbers, this might not be a valid international number
  return cleaned;
};

const WhatsAppChat = ({ phoneNumber, whatsappGroup, whatsappGroupName }) => {
  // Color mode values - all hooks must be at the top level
  const bgColor = useColorModeValue('white', 'gray.800');
  const inputBgColor = useColorModeValue('white', 'gray.700');
  const inputTextColor = useColorModeValue('black', 'white');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const mutedTextColor = useColorModeValue('gray.600', 'gray.400');
  const systemMsgBg = useColorModeValue('gray.50', 'gray.600');
  const systemMsgText = useColorModeValue('gray.600', 'gray.200');
  const systemMsgTimeText = useColorModeValue('gray.500', 'gray.400');
  const outgoingMsgBg = useColorModeValue('#123d2a', '#2f3436');
  const outgoingMsgText = useColorModeValue('white', 'white');
  const incomingMsgBg = useColorModeValue('gray.100', 'gray.600');
  const incomingMsgText = useColorModeValue('black', 'white');
  const authorNameColor = useColorModeValue('blue.500', 'blue.300');
  const whiteAlphaText = useColorModeValue('whiteAlpha.700', 'whiteAlpha.500');
  const headerBg = useColorModeValue('#123d2a', '#2f3436');
  const headerText = useColorModeValue('white', 'white');
  const hoverBg = useColorModeValue('gray.100', 'gray.700');
  const whiteAlphaHoverBg = useColorModeValue('whiteAlpha.300', 'whiteAlpha.200');
  const buttonHoverBg = useColorModeValue('gray.100', 'gray.600');
  const linkColor = useColorModeValue('blue.500', 'blue.300');
  const linkHoverColor = useColorModeValue('blue.600', 'blue.400');
  const whatsappIconColor = useColorModeValue('#123d2a', '#2f3436');

  const [message, setMessage] = useState('');
  const messagesEndRef = React.useRef(null);
  const chatContainerRef = React.useRef(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState(null);
  const [currentSequence, setCurrentSequence] = useState([]);
  const [sequenceIndex, setSequenceIndex] = useState(0);
  // Always start with personal chat
  const [chatTarget, setChatTarget] = useState('personal');
  const toast = useToast();

  const showToast = (title, description, status = 'info') => {
    toast({
      title,
      description,
      status,
      duration: 3000,
      isClosable: true,
      position: 'top-right'
    });
  };

  const fetchChatHistory = async (isRefresh = false) => {
    if (!phoneNumber) return;

    if (isRefresh) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }
    setError(null);
    
    try {
      // Clear existing chat history before fetching new one
      setChatHistory([]);
      
      // Get the correct chat ID based on selected chat type
      let chatId;
      if (chatTarget === 'group') {
        if (!whatsappGroup) {
          setError('No WhatsApp group available');
          return;
        }
        chatId = whatsappGroup;
      } else {
        // Personal chat - ensure we have a valid phone number
        if (!phoneNumber) {
          setError('No phone number available');
          return;
        }
        chatId = formatPhoneNumber(phoneNumber);
      }
      
      console.log(`[WhatsAppChat] Fetching ${chatTarget} messages for:`, chatId);
      const response = await WhatsAppService.getMessages(chatId, 10);

      // Messages are already filtered and sorted by timestamp (oldest first) from the service
      const messages = Array.isArray(response) ? response : [];
      console.log('[WhatsAppChat] Received messages:', messages.length);
      
      if (messages.length > 0) {
        // Update chat history with messages in chronological order (oldest first)
        setChatHistory(messages);
        
        if (isRefresh) {
          showToast('Chat Updated', 'New messages received', 'success');
        }
      } else {
        if (!isRefresh) {
          // Show appropriate message based on chat type
          const noMessagesText = chatTarget === 'group' 
            ? 'No group messages yet. Start the conversation!'
            : 'No previous messages. Send an introduction to start the conversation!';
          setError(noMessagesText);
        }
      }
    } catch (err) {
      console.error('Error fetching WhatsApp chat:', err);
      setError('Failed to load chat history');
      toast({
        title: 'Error',
        description: 'Could not load WhatsApp chat history',
        status: 'error',
        duration: 3000,
      });
    } finally {
      if (isRefresh) {
        setIsRefreshing(false);
      } else {
        setIsLoading(false);
      }
    }
  };

  const scrollToBottom = (instant = false) => {
    messagesEndRef.current?.scrollIntoView({ behavior: instant ? "instant" : "smooth" });
  };

  const handleScroll = () => {
    if (!chatContainerRef.current) return;
    
    const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
    setShowScrollButton(!isNearBottom);
  };

  useEffect(() => {
    if (phoneNumber) {
      // Reset to personal chat when phone number changes
      setChatTarget('personal');
      fetchChatHistory();
    }
  }, [phoneNumber]);

  useEffect(() => {
    if (whatsappGroup) {
      // Switch to group chat when group is created
      setChatTarget('group');
      fetchChatHistory();
    }
  }, [whatsappGroup]);

  useEffect(() => {
    if (chatHistory.length > 0) {
      // Use instant scroll on initial load, smooth scroll for updates
      scrollToBottom(true);
    }
  }, [chatHistory]);

  const handleSendMessage = async () => {
    if (!message.trim()) return;
    
    // Validate we have required info for the selected chat type
    if (chatTarget === 'personal' && !phoneNumber) {
      showToast('Error', 'No phone number available for personal chat', 'error');
      return;
    } else if (chatTarget === 'group' && !whatsappGroup) {
      showToast('Error', 'No group chat available', 'error');
      return;
    }
    
    const chatId = chatTarget === 'group' ? whatsappGroup : formatPhoneNumber(phoneNumber);
    
    setIsSending(true);
    try {
      await WhatsAppService.sendTextMessage(chatId, message.trim());
      
      // Clear the input field immediately for better UX
      setMessage('');
      
      // Refresh chat history after sending message
      await fetchChatHistory(true);
      scrollToBottom();
      
      showToast('Message Sent', 'Your message has been sent successfully', 'success');

      // Check if we're in a sequence and handle next message
      if (currentSequence.length > 0 && sequenceIndex < currentSequence.length - 1) {
        const nextIndex = sequenceIndex + 1;
        const nextTemplate = MESSAGE_TEMPLATES.find(t => t.id === currentSequence[nextIndex]);
        if (nextTemplate) {
          setTimeout(() => {
            setSequenceIndex(nextIndex);
            setMessage(nextTemplate.text);
            // Focus the input field for the next message
            document.querySelector('textarea[aria-label="Type a message"]')?.focus();
          }, 1000);
        }
      } else {
        // Reset sequence when done
        setCurrentSequence([]);
        setSequenceIndex(0);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      showToast('Error', 'Failed to send message', 'error');
      // Reset sequence on error
      setCurrentSequence([]);
      setSequenceIndex(0);
    } finally {
      setIsSending(false);
    }
  };

  const handleSendIntroMessage = async () => {
    // Only allow intro sequence for personal chats
    if (chatTarget !== 'personal') {
      showToast('Error', 'Introduction sequence is only available for personal chats', 'warning');
      return;
    }
    
    if (!phoneNumber || isSending) {
      showToast('Error', 'No phone number available for personal chat', 'error');
      return;
    }

    setIsSending(true);
    try {
      const sequence = ['intro', 'requirements', 'portfolio'];
      const formattedPhone = formatPhoneNumber(phoneNumber);

      // Send all messages in sequence
      for (const templateId of sequence) {
        const template = MESSAGE_TEMPLATES.find(t => t.id === templateId);
        if (template) {
          await WhatsAppService.sendTextMessage(formattedPhone, template.text);
          // Add a small delay between messages to prevent rate limiting
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      // Fetch final chat history and show success
      await fetchChatHistory(true);
      showToast('Messages Sent', 'All introduction messages sent successfully', 'success');
    } catch (error) {
      console.error('Error sending intro messages:', error);
      showToast('Error', 'Failed to send introduction messages', 'error');
    } finally {
      setIsSending(false);
    }
  };

  const formatWhatsAppMessage = (text) => {
    // Split text into lines and process each line
    return text.split('\n').map((line, lineIndex) => {
      let segments = [];
      let currentIndex = 0;

      // Process bold text (*text*)
      const boldRegex = /\*(.*?)\*/g;
      let boldMatch;
      while ((boldMatch = boldRegex.exec(line)) !== null) {
        // Add text before the bold section
        if (boldMatch.index > currentIndex) {
          segments.push(
            <Text as="span" key={`text-${currentIndex}`}>
              {line.slice(currentIndex, boldMatch.index)}
            </Text>
          );
        }
        // Add bold text
        segments.push(
          <Text as="span" fontWeight="bold" key={`bold-${boldMatch.index}`}>
            {boldMatch[1]}
          </Text>
        );
        currentIndex = boldMatch.index + boldMatch[0].length;
      }

      // Add remaining text after last bold section
      if (currentIndex < line.length) {
        const remainingText = line.slice(currentIndex);
        // Process italic text (_text_) in remaining text
        const italicRegex = /_(.*?)_/g;
        let lastIndex = 0;
        let italicMatch;
        
        while ((italicMatch = italicRegex.exec(remainingText)) !== null) {
          if (italicMatch.index > lastIndex) {
            segments.push(
              <Text as="span" key={`text-${currentIndex + lastIndex}`}>
                {remainingText.slice(lastIndex, italicMatch.index)}
              </Text>
            );
          }
          segments.push(
            <Text as="span" fontStyle="italic" key={`italic-${currentIndex + italicMatch.index}`}>
              {italicMatch[1]}
            </Text>
          );
          lastIndex = italicMatch.index + italicMatch[0].length;
        }
        
        if (lastIndex < remainingText.length) {
          segments.push(
            <Text as="span" key={`text-${currentIndex + lastIndex}`}>
              {remainingText.slice(lastIndex)}
            </Text>
          );
        }
      }

      // Return line with proper spacing
      return (
        <Text key={`line-${lineIndex}`} display="block">
          {segments.length > 0 ? segments : ' '}
        </Text>
      );
    });
  };

  const getMessageStatus = (ackName) => {
    switch (ackName) {
      case 'READ': return '✓✓ Read';
      case 'PLAYED': return '✓✓ Played';
      case 'DEVICE': return '✓✓ Delivered';
      case 'SERVER': return '✓ Sent';
      case 'PENDING': return '🕒 Pending';
      default: return '';
    }
  };

  const MessageBubble = ({ message }) => {
    const isFromMe = message.fromMe;
    const hasFile = message.hasMedia && message.mediaUrl;
    const isGroupSystemMessage = message.type === 'gp2';
    
    // Handle group system messages
    if (isGroupSystemMessage) {
      return (
        <Flex justify="center" w="100%" mb={2}>
          <Box
            bg={systemMsgBg}
            px={3}
            py={2}
            borderRadius="lg"
            maxW="80%"
            textAlign="center"
          >
            <Text fontSize="sm" color={systemMsgText}>
              {message.subtype === 'create' ? (
                <Text as="span">
                  <Text as="span" fontWeight="bold">Group Created:</Text>
                  {' '}{message.body}
                </Text>
              ) : message.body}
            </Text>
            <Text fontSize="xs" color={systemMsgTimeText} mt={1}>
              {moment.unix(message.timestamp).format('MMM D, h:mm A')}
            </Text>
          </Box>
        </Flex>
      );
    }
    
    // Handle regular messages
    return (
      <Flex justify={isFromMe ? 'flex-end' : 'flex-start'} w="100%" mb={2}>
        <Box
          bg={isFromMe ? outgoingMsgBg : incomingMsgBg}
          color={isFromMe ? outgoingMsgText : incomingMsgText}
          px={3}
          py={2}
          borderRadius="lg"
          maxW="80%"
          whiteSpace="pre-wrap"
        >
          {message.author && !isFromMe && (
            <Text fontSize="xs" fontWeight="bold" mb={1} color={authorNameColor}>
              {message.author.name || message.author.id.split('@')[0]}
            </Text>
          )}
          
          {hasFile && (
            <Link 
              href={message.mediaUrl}
              isExternal
              display="block"
              mb={2}
              color={isFromMe ? outgoingMsgText : linkColor}
              textDecoration="underline"
              _hover={{ color: isFromMe ? whiteAlphaText : linkHoverColor }}
            >
              <Flex align="center">
                <Icon as={FiDownload} mr={2} />
                {message.fileName || 'Download File'}
              </Flex>
            </Link>
          )}
          <Box fontSize="sm">
            {formatWhatsAppMessage(message.body)}
          </Box>
          <Flex justify="space-between" align="center" mt={1}>
            <Text fontSize="xs" color={isFromMe ? whiteAlphaText : mutedTextColor}>
              {moment.unix(message.timestamp).format('MMM D, h:mm A')}
            </Text>
            {isFromMe && message.ackName && (
              <Text fontSize="xs" color={whiteAlphaText} ml={2}>
                {getMessageStatus(message.ackName)}
              </Text>
            )}
          </Flex>
        </Box>
      </Flex>
    );
  };

  if (isLoading) {
    return (
      <VStack h="100%" justify="center" bg={bgColor} borderRadius="md" boxShadow="sm">
        <Spinner />
        <Text>Loading chat history...</Text>
      </VStack>
    );
  }

  if (error || chatHistory.length === 0) {
    return (
      <VStack h="100%" spacing={0} bg={bgColor} borderRadius="md" boxShadow="sm">
        <Flex
          w="100%"
          bg={headerBg}
          color={headerText}
          p={3}
          borderTopRadius="md"
          align="center"
          justify="space-between"
        >
          <Flex align="center">
            <FaWhatsapp size={24} />
            <Text ml={2} fontWeight="bold">
              {chatTarget === 'personal' ? 'Personal Chat' : (whatsappGroupName || 'Group Chat')}
            </Text>
          </Flex>
          <IconButton
            icon={<FaSync />}
            isLoading={isRefreshing}
            onClick={() => fetchChatHistory(true)}
            aria-label="Refresh chat"
            size="sm"
            color={headerText}
            variant="ghost"
            _hover={{ bg: whiteAlphaHoverBg }}
          />
        </Flex>
        <VStack flex={1} justify="center" spacing={4} p={4}>
          <FaWhatsapp size={40} color={whatsappIconColor} />
          <Text>{error || 'No chat history found'}</Text>
          <Button
            leftIcon={<FaWhatsapp />}
            bg={headerBg}
            color={headerText}
            onClick={handleSendIntroMessage}
            isLoading={isSending}
            loadingText="Sending Messages..."
          >
            Send All Intro Messages
          </Button>
        </VStack>
      </VStack>
    );
  }

  return (
    <VStack h="100%" spacing={0} bg={bgColor} borderRadius="md" boxShadow="sm">
      {/* Chat Toggle */}
      {whatsappGroup && (
        <Flex p={2} bg={headerBg}>
          <Button
            size="sm"
            variant={chatTarget === 'personal' ? 'solid' : 'outline'}
            bg={headerBg}
            color={headerText}
            flex="1"
            mr={1}
            onClick={() => {
              setChatHistory([]);
              setError(null);
              setMessage('');
              setChatTarget('personal');
              fetchChatHistory();
            }}
            leftIcon={<FaWhatsapp />}
          >
            Personal Chat
          </Button>
          <Button
            size="sm"
            variant={chatTarget === 'group' ? 'solid' : 'outline'}
            bg={headerBg}
            color={headerText}
            flex="1"
            ml={1}
            onClick={() => {
              setChatHistory([]);
              setError(null);
              setMessage('');
              setChatTarget('group');
              fetchChatHistory();
            }}
            leftIcon={<Icon as={FaWhatsapp} transform="scale(1.2)" />}
          >
            Group Chat
          </Button>
        </Flex>
      )}

      <Flex
        w="100%"
        bg={headerBg}
        color={headerText}
        p={3}
        borderTopRadius={!whatsappGroup && "md"}
        align="center"
        justify="space-between"
      >
        <Flex align="center">
          <FaWhatsapp size={24} />
          <Text ml={2} fontWeight="bold">
            {chatTarget === 'personal' ? 'Personal Chat' : (whatsappGroupName || 'Group Chat')}
          </Text>
          {chatHistory.length > 0 && (
            <Badge ml={2} bg={whiteAlphaHoverBg}>{chatHistory.length} messages</Badge>
          )}
        </Flex>
        <IconButton
          icon={<FaSync />}
          isLoading={isRefreshing}
          onClick={() => fetchChatHistory(true)}
          aria-label="Refresh chat"
          size="sm"
          color={headerText}
          variant="ghost"
          _hover={{ bg: whiteAlphaHoverBg }}
        />
      </Flex>
      <VStack
        ref={chatContainerRef}
        flex={1}
        w="100%"
        overflowY="auto"
        spacing={4}
        p={4}
        css={{
          '&::-webkit-scrollbar': {
            width: '4px',
          },
          '&::-webkit-scrollbar-track': {
            width: '6px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#718096',
            borderRadius: '24px',
          },
        }}
      >
        {chatHistory.map((message) => (
          <MessageBubble key={message.id} message={message} />
        ))}
        <div ref={messagesEndRef} />
      </VStack>

      {/* Message Input Section */}
      <Box w="100%" mt="auto" bg={bgColor} p={3} borderTop="1px" borderColor={borderColor}>
        <Flex gap={2}>
          <Menu placement="top">
            <MenuButton
              as={IconButton}
              icon={<FaChevronDown />}
              variant="outline"
              size="md"
              aria-label="Select template"
              borderColor={borderColor}
              color={inputTextColor}
              bg={inputBgColor}
              _hover={{ bg: buttonHoverBg }}
            />
            <MenuList bg={bgColor} borderColor={borderColor}>
              {MESSAGE_TEMPLATES.map(template => (
                <MenuItem
                  key={template.id}
                  onClick={() => setMessage(template.text)}
                  _hover={{ bg: hoverBg }}
                >
                  {template.title}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
          <InputGroup size="md" flexGrow={1}>
            <Textarea
              pr="3rem"
              value={message}
              onChange={(e) => {
                setMessage(e.target.value);
                // Automatically adjust height based on content
                e.target.style.height = 'auto';
                e.target.style.height = `${Math.min(e.target.scrollHeight, 8 * 24)}px`; // 24px per row
              }}
              placeholder="Type a message..."
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              minH="60px"
              maxH="192px" // 8 rows * 24px per row
              resize="none"
              rows={2}
              overflowY="auto"
              bg={inputBgColor}
              color={inputTextColor}
              borderColor={borderColor}
              fontSize="md"
              p={2}
              lineHeight="24px"
              _placeholder={{ color: mutedTextColor }}
              _hover={{ borderColor: borderColor }}
              _focus={{ borderColor: linkColor, boxShadow: 'none' }}
              css={{
                '&::-webkit-scrollbar': {
                  width: '4px',
                },
                '&::-webkit-scrollbar-track': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#718096',
                  borderRadius: '24px',
                },
              }}
            />
            <InputRightElement h="100%" pr={2}>
              <IconButton
                h="2rem"
                size="md"
                icon={<FaPaperPlane />}
                onClick={handleSendMessage}
                isDisabled={!message.trim()}
                color={inputTextColor}
                variant="ghost"
                _hover={{ bg: buttonHoverBg }}
              />
            </InputRightElement>
          </InputGroup>
        </Flex>
      </Box>
    </VStack>
  );
};

export default WhatsAppChat;
